import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/fmt";
import { describe } from "@/components/crm/Timeline";
import { TaskLine, LeadLink, human } from "./TaskLine";

const PROOF = { note: "Note", email_sent: "Email", call: "Call", call_logged: "Call", meeting_booked: "Meet booked", meeting_status: "Meeting marked", whatsapp: "WhatsApp" };
const dayKey = (iso) => fmtDate(iso);

function Proof({ p }) {
  if (!p.length) return null;
  return (
    <div className="mt-1 text-[var(--text-dim)]">
      Proof found:{" "}
      {p.map((x, i) => <span key={i} className="mr-2 text-[var(--ok)]">{PROOF[x.kind] || human(x.kind)} at {fmtDateTime(x.at)}{x.text ? ` (“${x.text}”)` : ""}</span>)}
    </div>
  );
}

function ByDay({ rows, render }) {
  const groups = useMemo(() => {
    const m = [];
    rows.forEach((r) => {
      const k = dayKey(r.done_at || r.created_at);
      if (!m.length || m[m.length - 1].k !== k) m.push({ k, rows: [] });
      m[m.length - 1].rows.push(r);
    });
    return m;
  }, [rows]);
  return groups.map((g) => (
    <div key={g.k} className="mt-3">
      <div className="text-[11px] uppercase tracking-wider text-[var(--text-dim)] border-b border-[var(--border)] pb-1">{g.k} · {g.rows.length}</div>
      {g.rows.map(render)}
    </div>
  ));
}

function ActLine({ a }) {
  const { title, body } = describe(a);
  return (
    <div className="py-2 border-b border-[var(--border)] last:border-0 text-xs" data-testid={`desk-act-${a._id}`}>
      <div className="flex flex-wrap gap-x-2 items-baseline">
        <span className="text-[var(--text-faint)] tabular-nums w-[62px]">{new Date(a.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
        <span className="text-sm">{title}</span>
        {a.lead_id && <><span className="text-[var(--text-dim)]">on</span><LeadLink id={a.lead_id} name={a.lead_name} /></>}
      </div>
      {body && <div className="ml-[70px] mt-0.5 whitespace-pre-line text-[var(--text-dim)]">{body}</div>}
    </div>
  );
}

export function PersonHistory({ email, days }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("tasks");
  const [q, setQ] = useState("");
  useEffect(() => {
    setD(null);
    api.get("/governance/history", { params: { email, days } }).then((r) => setD(r.data));
  }, [email, days]);
  const match = (x) => !q || JSON.stringify([x.lead_name, x.label, x.note, x.kind, x.text, x.outcome]).toLowerCase().includes(q.toLowerCase());
  if (!d) return <div className="text-xs text-[var(--text-dim)] mt-3">Loading history…</div>;
  const tasks = d.tasks.filter(match), acts = d.activity.filter(match);
  return (
    <div className="mt-4 border-t border-[var(--border)] pt-3" data-testid={`history-${email}`}>
      <div className="flex flex-wrap items-center gap-3">
        {[["tasks", `Tasks done (${d.tasks.length})`], ["activity", `Everything he did (${d.activity.length})`]].map(([k, l]) => (
          <button key={k} className={`text-sm ${tab === k ? "font-medium border-b-2 border-[var(--ai)]" : "text-[var(--text-dim)]"}`} onClick={() => setTab(k)} data-testid={`history-tab-${k}`}>{l}</button>
        ))}
        <input className="ml-auto px-2.5 py-1 rounded border text-xs w-[200px]" placeholder="Search lead, task, note…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`history-search-${email}`} />
      </div>
      {tab === "tasks"
        ? (tasks.length ? <ByDay rows={tasks} render={(t) => <TaskLine key={t.task_id} t={t} extra={<Proof p={t.proof} />} />} /> : <div className="text-xs text-[var(--text-dim)] mt-3">No tasks done in this period.</div>)
        : (acts.length ? <ByDay rows={acts} render={(a) => <ActLine key={a._id} a={a} />} /> : <div className="text-xs text-[var(--text-dim)] mt-3">Nothing logged in this period.</div>)}
    </div>
  );
}
